/**
 * useFaqTokens - values for the FAQ answer tokens (contract v1.2 §3.13.3)
 *
 * Filled at render time from the same sources the rest of the site uses, so
 * an answer never shows a stale price or policy value:
 * - {{price:<slug>}}: the service catalog (useServices: the static launch
 *   prices at once, the server's fees once GET /api/public/services answers)
 * - {{email}}: config.business.contactEmail
 * - {{cancel_hours}}, {{meeting_platform}}, {{min_client_age}}: PRACTICE
 * - {{late_cancel_fee}}, {{payment_timing}}: data/practice.js wording
 *
 * @example
 * const tokens = useFaqTokens();
 * <MarkdownText source={answer} tokens={tokens} />
 */

import { useMemo } from 'react';
import config from '../config';
import { PRACTICE, lateCancelFeeText, paymentTimingText } from '../data/practice';
import useServices from './useServices';

/** 170 -> "$170", 0 -> "Free", 62.5 -> "$62.50"; null when not a number. */
export const formatFee = (amount) => {
  const n = Number(amount);
  if (amount === null || amount === undefined || !Number.isFinite(n)) return null;
  if (n === 0) return 'Free';
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
};

/**
 * Token values for MarkdownText / fillTokens.
 * @param {Array} services - Merged service catalog (useServices().services)
 * @returns {object}
 */
export const faqTokenValues = (services) => ({
  email: config.business.contactEmail,
  cancel_hours: String(PRACTICE.cancellationWindowHours),
  late_cancel_fee: lateCancelFeeText(),
  payment_timing: paymentTimingText(),
  meeting_platform: PRACTICE.meetingPlatform,
  min_client_age: String(PRACTICE.minClientAge),
  price: (slug) => {
    const service = (services || []).find((s) => s.slug === slug);
    return service ? formatFee(service.price) : null;
  },
});

const useFaqTokens = () => {
  const { services } = useServices();
  return useMemo(() => faqTokenValues(services), [services]);
};

export default useFaqTokens;
